// Joins canonical camera Media activities into an existing activity feed.
// The camera list pages separately, so a row is only shown inside the range the
// host feed has actually loaded (on/after `boundary` while the host has more
// pages); nothing outside it is silently presented as complete. Ids are
// canonical, so a session appears once however many times it is refetched.
export const isCameraMediaActivity = activity => activity?.data?.video?.ref?.kind === 'camera_session';
// A persisted parent (daily Live or a named/selected Media) the owner may complete/reopen.
export const isLinkedCameraParent = activity => isCameraMediaActivity(activity) && Object.prototype.hasOwnProperty.call(activity, 'due_at');
// Daily Live identity is the owner's civil day at explicit Start, not a UTC
// instant to re-zone after travel/profile changes. Named/legacy parents retain
// their timestamp-based placement because they have no persisted capture day.
export const cameraCaptureDay = activity => isLinkedCameraParent(activity) && /^\d{4}-\d{2}-\d{2}$/.test(activity.capture_day || '')
  ? activity.capture_day : null;
// Day placement of a camera row: a linked parent stays on its capture/scheduled
// day however much later it is completed; legacy unlinked rows use capture start.
export const cameraPlacementDate = activity => (isLinkedCameraParent(activity)
  ? activity.due_at || activity.created_at
  : activity.due_at || activity.completed_at || activity.created_at);

// Rows are one per exact video. Videos of one parent (a daily Live Media) join
// that parent once, in feed order; a live video represents it in compact lanes.
// Same-named devices are distinct videos: identity is the typed reference.
// A parent with a single video is passed through unchanged.
export function groupCameraMedia(rows) {
  const parents = new Map();
  for (const row of rows) {
    const key = String(row._id), group = parents.get(key);
    if (!group) parents.set(key, [row]);
    else if (String(group[0].user_id) === String(row.user_id) && !group.some(r => r.data.video.ref.id === row.data.video.ref.id)) group.push(row);
  }
  return [...parents.values()].map(group => {
    if (group.length === 1) return group[0];
    const videos = group.map(r => r.data.video);
    return { ...group[0], data: { ...group[0].data, video: videos.find(v => v.state === 'live') || videos[0], videos } };
  });
}

export function mergeCameraMedia(base, camera, { boundary = null, startDate = null, endDate = null, authorIds = null, dateKey = null } = {}) {
  if (!camera?.length) return base;
  camera = groupCameraMedia(camera);
  const overlays = new Map(camera.map(a => [String(a._id), a]));
  const parents = base.map(a => {
    const overlay = overlays.get(String(a._id));
    return overlay && String(overlay.user_id) === String(a.user_id)
      ? { ...a, ...overlay, data: { ...a.data, ...overlay.data } } : a;
  });
  const known = new Set(parents.map(a => String(a._id)));
  const from = startDate ? new Date(startDate).getTime() : -Infinity;
  const to = endDate ? new Date(endDate).getTime() : Infinity;
  const floor = boundary ? new Date(boundary).getTime() : -Infinity;
  const joined = camera.filter(a => {
    const date = cameraPlacementDate(a);
    const at = new Date(date).getTime();
    const day = cameraCaptureDay(a) || dateKey?.(date);
    const beforeStart = dateKey && /^\d{4}-\d{2}-\d{2}$/.test(startDate || '') ? day < startDate : at < from;
    const afterEnd = dateKey && /^\d{4}-\d{2}-\d{2}$/.test(endDate || '') ? day > endDate : at > to;
    if (known.has(a._id) || at < floor || beforeStart || afterEnd) return false;
    if (authorIds && !authorIds.has(String(a.user_id))) return false;
    known.add(a._id);
    return true;
  });
  return joined.length ? [...parents, ...joined] : parents;
}
